import React, { useRef } from "react";
import * as Sentry from "@sentry/react";
import { Button, ButtonVariant } from "@merge-api/merge-javascript-shared";
import useAppContext from "../../../context/useAppContext";
import { useFrigadeAssistantEnabled } from "../../../shared/hooks/useFrigadeAssistantEnabled";
import { showErrorToast } from "../../../shared/Toasts";
import { IssueDescription } from "../../../../constants";
import { IntegrationIssue } from "../../IntegrationsManagementEntities";

const KNOWN_ISSUE_DESCRIPTIONS = Object.values(IssueDescription).map((description) =>
  description.toLowerCase(),
);

interface Props {
  integrationIssue: IntegrationIssue;
}

const HelpMeSolveButton = ({ integrationIssue }: Props) => {
  // state
  const hasReportedAskUnavailable = useRef(false);

  // hooks
  const { user } = useAppContext();
  const { enabled: frigadeAssistantEnabled } = useFrigadeAssistantEnabled(user.organization.id);

  // event handlers
  // Only error descriptions matching the Merge-authored IssueDescription set go into the
  // question; anything else may be customer free text, which must not be sent to Frigade
  // (the reason titleSensitive masks it from the Assistant's DOM snapshot). The match is
  // case-insensitive because stored issues carry non-canonical casing like "Bad API Key".
  const clickHelpMeSolveButton = () => {
    const assistant = window.FrigadeAssistant;
    if (typeof assistant?.ask !== "function") {
      // A failed CDN load is permanent for this page session, so do not present it as
      // transient; FrigadeAssistant.tsx sets the flag from the script tag's onerror.
      if (!hasReportedAskUnavailable.current) {
        hasReportedAskUnavailable.current = true;
        Sentry.captureMessage(
          window.__mergeFrigadeAssistantLoadFailed
            ? "Frigade Assistant ask clicked after the SDK failed to load"
            : "Frigade Assistant ask clicked before the SDK loaded",
        );
      }
      showErrorToast(
        window.__mergeFrigadeAssistantLoadFailed
          ? "The AI assistant failed to load. Refresh the page to try again."
          : "The AI assistant is still loading. Try again in a moment.",
      );
      return;
    }
    const errorDescription = integrationIssue.error_description;
    const integrationName = integrationIssue.linked_account.integration.name;
    assistant.ask({
      question: KNOWN_ISSUE_DESCRIPTIONS.includes(errorDescription?.toLowerCase())
        ? `What does the "${errorDescription}" error with ${integrationName} mean?`
        : `What does this error with ${integrationName} mean?`,
    });
  };

  // render
  if (!frigadeAssistantEnabled) return null;

  return (
    <Button
      variant={ButtonVariant.TertiaryWhite}
      size="sm"
      className="mt-4"
      onClick={clickHelpMeSolveButton}
    >
      Solve with AI
    </Button>
  );
};

export default HelpMeSolveButton;
